import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  mfgCatalogApi,
  mfgCatalogKeys,
  useMfgGrains,
  useMfgUoms,
  type MfgItemDetail,
  type UpdateMfgItemBody,
  type MfgMaterialSpec,
  type MfgWasteSpec,
  type MfgVariantSpec,
  type MfgAlternativeSpec,
} from "@/lib/mfg-catalog";
import { ApiError } from "@/lib/api";

const num = (s: string): number | null => (s.trim() === "" ? null : Number(s));

export function SubtypeForm({ item }: { item: MfgItemDetail }) {
  const qc = useQueryClient();
  const { data: grains } = useMfgGrains();
  const { data: uoms } = useMfgUoms();
  // Local state per subtype kind; only the block matching item's type is rendered.
  const [mat, setMat] = useState<MfgMaterialSpec>(item.material ?? { width: null, length: null, thickness: null, materialGrainCode: null, unitOfMeasureCode: null });
  const [waste, setWaste] = useState<MfgWasteSpec>(item.waste ?? { itemCutoutMarginMm: null, machineGrabWidthMm: null, machineGrabLengthMm: null, startRunLengthMm: null, endRunLengthMm: null, percentageWaste: null });
  const [variant, setVariant] = useState<MfgVariantSpec>(item.variant ?? { name: null, minimumChoice: 1, maximumChoice: 1 });
  const [alt, setAlt] = useState<MfgAlternativeSpec>(item.alternative ?? { name: null });

  const spine: Omit<UpdateMfgItemBody, "material" | "waste" | "variant" | "alternative"> = {
    name: item.name, itemNumber: item.itemNumber, description: item.description, unitOfMeasureCode: item.unitOfMeasureCode,
  };

  const save = useMutation({
    mutationFn: (block: Pick<UpdateMfgItemBody, "material" | "waste" | "variant" | "alternative">) =>
      mfgCatalogApi.update(item.mfgItemId, { ...spine, ...block }, item.rowVersionBase64),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: mfgCatalogKeys.detail(item.mfgItemId) }); toast.success("Saved"); },
    onError: (e) => toast.error(e instanceof ApiError && e.status === 409 ? "Changed since you opened it — reload." : `Save failed: ${e.message}`),
  });

  const numInput = (label: string, v: number | null, on: (n: number | null) => void) => (
    <div><Label>{label}</Label><Input type="number" value={v ?? ""} onChange={(e) => on(num(e.target.value))} /></div>
  );

  if (item.itemTypeCode === "Material") return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-muted-foreground">Geometry (Material)</h3>
      <div className="grid grid-cols-3 gap-3">
        {numInput("Width (mm)", mat.width, (n) => setMat({ ...mat, width: n }))}
        {numInput("Length (mm)", mat.length, (n) => setMat({ ...mat, length: n }))}
        {numInput("Thickness (mm)", mat.thickness, (n) => setMat({ ...mat, thickness: n }))}
        <div><Label>Grain</Label>
          <select className="h-9 w-full rounded-md border px-2 text-sm" value={mat.materialGrainCode ?? ""} onChange={(e) => setMat({ ...mat, materialGrainCode: e.target.value || null })}>
            <option value="">—</option>
            {(grains ?? []).map((g) => <option key={g.mfgMaterialGrainCode} value={g.mfgMaterialGrainCode}>{g.name}</option>)}
          </select>
        </div>
        <div><Label>UoM</Label>
          <select className="h-9 w-full rounded-md border px-2 text-sm" value={mat.unitOfMeasureCode ?? ""} onChange={(e) => setMat({ ...mat, unitOfMeasureCode: e.target.value || null })}>
            <option value="">—</option>
            {(uoms ?? []).map((u) => <option key={u.mfgUnitOfMeasureCode} value={u.mfgUnitOfMeasureCode}>{u.name}</option>)}
          </select>
        </div>
      </div>
      <Button onClick={() => save.mutate({ material: mat, waste: null, variant: null, alternative: null })} disabled={save.isPending}>Save geometry</Button>
    </section>
  );

  if (item.itemTypeCode === "Waste") return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-muted-foreground">Nesting (Waste)</h3>
      <div className="grid grid-cols-3 gap-3">
        {numInput("Cutout margin (mm)", waste.itemCutoutMarginMm, (n) => setWaste({ ...waste, itemCutoutMarginMm: n }))}
        {numInput("Grab width (mm)", waste.machineGrabWidthMm, (n) => setWaste({ ...waste, machineGrabWidthMm: n }))}
        {numInput("Grab length (mm)", waste.machineGrabLengthMm, (n) => setWaste({ ...waste, machineGrabLengthMm: n }))}
        {numInput("Start run (mm)", waste.startRunLengthMm, (n) => setWaste({ ...waste, startRunLengthMm: n }))}
        {numInput("End run (mm)", waste.endRunLengthMm, (n) => setWaste({ ...waste, endRunLengthMm: n }))}
        {numInput("Waste %", waste.percentageWaste, (n) => setWaste({ ...waste, percentageWaste: n }))}
      </div>
      <Button onClick={() => save.mutate({ material: null, waste, variant: null, alternative: null })} disabled={save.isPending}>Save nesting</Button>
    </section>
  );

  if (item.itemTypeCode === "Variant") return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-muted-foreground">Variant</h3>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-1"><Label>Group name</Label><Input value={variant.name ?? ""} onChange={(e) => setVariant({ ...variant, name: e.target.value || null })} /></div>
        {numInput("Min choice", variant.minimumChoice, (n) => setVariant({ ...variant, minimumChoice: n ?? 0 }))}
        {numInput("Max choice", variant.maximumChoice, (n) => setVariant({ ...variant, maximumChoice: n ?? 1 }))}
      </div>
      <Button onClick={() => save.mutate({ material: null, waste: null, variant, alternative: null })} disabled={save.isPending}>Save variant</Button>
    </section>
  );

  if (item.itemTypeCode === "Alternative") return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-muted-foreground">Alternative</h3>
      <div><Label>Group name</Label><Input value={alt.name ?? ""} onChange={(e) => setAlt({ name: e.target.value || null })} /></div>
      <Button onClick={() => save.mutate({ material: null, waste: null, variant: null, alternative: alt })} disabled={save.isPending}>Save alternative</Button>
    </section>
  );

  return null; // spine-only types (Assembly/Component/Labour/Process/Equipment)
}
